import { useApolloClient } from '@apollo/client';
import {
  LoadAiQueryDocument,
  LoadAiQueryQuery,
  LoadAiQueryQueryVariables,
} from '@warebee/frontend/data-access-api-graphql';
import { BatchJobStatus } from '@warebee/shared/engine-model';
import { nanoid } from 'nanoid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilCallback } from 'recoil';
import { aiAgentMessageById } from '../../aiAgent/store/aiAgent.state';
import { AI_QUERY_REFRESH_INTERVAL } from '../../common/constants';
import { errorAppender } from '../../store/error.state';

export type LoadAiQueryParams = {
  id: string;
};

function useLoadAiQuery() {
  const { t } = useTranslation('errors');
  const errorTitle = t`Cannot process imported file`;
  const client = useApolloClient();
  const [observable, setObservable] = useState<ZenObservable.Subscription>();
  let timeoutId = null;

  const initLoading = useRecoilCallback(
    ({ snapshot, set }) =>
      async (params: LoadAiQueryParams) => {
        set(aiAgentMessageById(params.id), current => ({
          ...current,
          status: BatchJobStatus.CALCULATING,
        }));
      },
  );

  const loadAiQuery = useRecoilCallback(
    ({ snapshot, set }) =>
      async (params: LoadAiQueryParams) => {
        const query = client.watchQuery<
          LoadAiQueryQuery,
          LoadAiQueryQueryVariables
        >({
          query: LoadAiQueryDocument,
          variables: {
            id: params.id,
          },
        });

        function handleError(details, stack) {
          console.error(errorTitle, details, stack);
          set(errorAppender, {
            id: nanoid(),
            title: errorTitle,
            details: details,
            callStack: stack,
          });
          //
        }
        const queryObservable = query.subscribe(
          async ({ data, errors }) => {
            if (errors) {
              const stack = errors.map(e => e.message).join('. ');
              handleError(null, stack);
              return;
            }

            const query = data.aiQuery;

            if (
              query.status === BatchJobStatus.FAILED ||
              query.status === BatchJobStatus.TERMINATED
            ) {
            }

            if (
              query.status === BatchJobStatus.CALCULATING ||
              query.status === BatchJobStatus.CREATED ||
              query.status === BatchJobStatus.RESTARTED
            ) {
              timeoutId = setTimeout(() => {
                loadAiQuery(params);
              }, AI_QUERY_REFRESH_INTERVAL);
            }

            set(aiAgentMessageById(params.id), query);
          },
          error => {
            console.error(error);
            handleError(error.message || error, error.stack || null);
          },
        );
        setObservable(queryObservable);
      },
  );

  async function call(params: LoadAiQueryParams) {
    await initLoading(params);
    await loadAiQuery(params);
  }

  function cancel() {
    observable?.unsubscribe();
    clearTimeout(timeoutId);
  }

  return [call, cancel] as const;
}

export default useLoadAiQuery;
